import React, { useEffect, useState } from "react";
import * as Yup from "yup";
import { connect } from "react-redux";
import { Field, Formik } from "formik";
import * as formsService from "../services/formsService";
import OnSubmitValidationError from "../components/FormComponents/OnSubmitValidationError";
import { InputField, SocialSecurityInputField, CTA } from "@coverme/forms";
import { DateInput } from "../components/FormComponents/DateInput";
import * as lookupActions from "../actions/lookupActions";
import Axios from "axios";
import { PersonMatchModal } from "../components/PersonMatchModal/PersonMatchModal";
import * as interviewActions from "../actions/interviewActions";
import moment from "moment";
import { getMatchingPerson } from "./InterviewWizard/services/personMatchService";
import { cloneEncounter } from "../actions/interviewActions";
import { withUser } from "@coverme/identity";
import { isCallCenterUser } from "../services/userService";
import { toast } from "react-toastify";

const validationSchema = Yup.object().shape({
    firstName: Yup.string()
        .nullable()
        .required("Required"),
    lastName: Yup.string()
        .nullable()
        .required("Required"),
    socialSecurityNumber: Yup.string()
        .nullable()
        .notRequired()
        .test("is-valid-ssn", "Invalid", value => value === "" || value == null || value?.length === 9),
    dateOfBirth: Yup.date()
        .min(
            moment()
                .subtract(120, "years")
                .format(),
            `Max 120 years`
        )
        .max(new Date(), `Invalid`)
        .required("Required")
        .typeError("Invalid"),
    nameSuffix: Yup.string()
        .nullable()
        .notRequired()
        .max(5),
});

const AddNewCase = ({
    lookups,
    customer,
    getLookups,
    history,
    setSimplePersonDemographics,
    setPatientHasNoId,
    user,
}) => {
    const [matchingPersons, setMatchingPersons] = useState([]);
    const [isModalShown, setIsModalShown] = useState(false);
    const [formData, setFormData] = useState({});
    const onSubmit = formData => {
        if (formData.documentType === "Other") {
            formData = {
                ...formData,
                documentType: formData.documentTypeOther,
            };

            delete formData.documentTypeOther;
        }

        setFormData(formData);

        if (formData.doesHaveId === false) {
            formData = { ...formData, ...{ documentType: null, documentId: null } };
        }

        getMatchingPerson(customer?.data?.customer?.GlobalId, formData).then(result => {
            setMatchingPersons(result.data);
            setIsModalShown(true);
        });
    };

    useEffect(() => {
        if (customer.isInitialized === false || customer.isLoading === true) {
            return;
        }
        if (lookups.isInitialized === false) {
            getLookups(customer?.data?.customer?.GlobalId, customer.data.jurisdictions);
        }
    }, [customer, getLookups, lookups.isInitialized]);

    const initialValues = {
        firstName: null,
        middleInitial: null,
        lastName: null,
        nameSuffix: null,
        socialSecurityNumber: history?.location?.appState?.ssn != null ? history?.location?.appState?.ssn : "",
        dateOfBirth: null,
    };

    return (
        <div className="wizard-page no-sidebar">
            <h2 className="title">New Case</h2>
            <PersonMatchModal
                isShown={isModalShown}
                persons={matchingPersons}
                history={history}
                user={user}
                onClose={() => {
                    setIsModalShown(false);
                }}
                onPersonVerify={(shouldStartNow, selectedPersonId) => {
                    Axios.get(
                        `/api/customer/${customer?.data?.customer?.GlobalId}/person/${selectedPersonId}/encounter/latest`
                    ).then(result => {
                        const encounterId = result.data;
                        return cloneEncounter(encounterId).then(newEncounterId => {
                            if (shouldStartNow) {
                                return history.push(`/interview/${newEncounterId}/entry`);
                            } else {
                                if (isCallCenterUser(user)) {
                                    toast.success(
                                        "A new case was created with person matching. Please continue the rest of the interview at the hospital"
                                    );
                                    return history.push(`/customerselection`);
                                }
                                setIsModalShown(false);
                                return history.push("/");
                            }
                        });
                    });
                }}
                onNoMatchFound={() => {
                    setSimplePersonDemographics(formData);
                    history.push("/interview/new/entry");
                }}
            />
            <Formik
                onSubmit={onSubmit}
                initialValues={initialValues}
                validationSchema={validationSchema}
                render={props => {
                    const { setFieldValue, setFieldTouched, resetForm, handleSubmit, errors, touched, values } = props;
                    const getValidationState = formsService.getValidationState.bind(this, errors, touched);
                    const getErrors = formsService.getErrors.bind(this, errors);
                    const isRequired = formsService.isRequired.bind(this, validationSchema, values);
                    return (
                        <form id="add-new-case" className="wizard-form">
                            <OnSubmitValidationError callback={() => {}} />
                            <div className="form-section">
                                <div className="form-control m-input">
                                    <Field
                                        name="firstName"
                                        render={({ field }) => (
                                            <InputField
                                                id="new_case_firstName"
                                                title={"First Name"}
                                                placeholder={"First Name"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control s-input">
                                    <Field
                                        name="middleInitial"
                                        render={({ field }) => (
                                            <InputField
                                                id="new_case_middleInitial"
                                                title={"MI"}
                                                maxLength={1}
                                                placeholder={"MI"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control m-input">
                                    <Field
                                        name="lastName"
                                        render={({ field }) => (
                                            <InputField
                                                id="new_case_lastName"
                                                title={"Last Name"}
                                                placeholder={"Last Name"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control s-input">
                                    <Field
                                        name="nameSuffix"
                                        render={({ field }) => (
                                            <InputField
                                                id="new_case_suffix"
                                                title={"Suffix"}
                                                placeholder={"Suffix"}
                                                errorMessage={getErrors(field)}
                                                validState={getValidationState(field)}
                                                required={isRequired(field)}
                                                maxLength={5}
                                                {...field}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="form-section">
                                <div className="form-control s-140-input no-upscope">
                                    <Field
                                        name="socialSecurityNumber"
                                        render={({ field }) => (
                                            <SocialSecurityInputField
                                                {...field}
                                                id="new_case_ssn"
                                                title={"SSN"}
                                                value={field.value}
                                                changeEvent={e => {
                                                    setFieldValue(field.name, e.substring(0, 9));
                                                }}
                                                errorMessage={getErrors(field)}
                                                required={isRequired(field)}
                                                validState={getValidationState(field)}
                                            />
                                        )}
                                    />
                                </div>
                                <div className="form-control s-160-input">
                                    <Field
                                        name="dateOfBirth"
                                        render={({ field }) => (
                                            <DateInput
                                                {...field}
                                                id="new_case_dob"
                                                title={"Date of Birth"}
                                                changeEvent={v => setFieldValue(field.name, v)}
                                                errorMessage={getErrors(field)}
                                                required={isRequired(field)}
                                                validState={getValidationState(field)}
                                            />
                                        )}
                                    />
                                </div>
                            </div>
                            <div className="wizard-buttons">
                                <CTA styleType={"blue"} size={"large"} click={handleSubmit}>
                                    Submit
                                </CTA>
                            </div>
                        </form>
                    );
                }}
            />
            <div className="required_field_indicator_explained"> Indicates required field</div>
        </div>
    );
};

export const AddNewCaseWithUser = withUser(AddNewCase);

export const AddNewCaseContainer = connect(
    state => ({
        lookups: state.interviewLookups,
        customer: state.customer,
    }),
    dispatch => ({
        getLookups: (customerId, jurisdiction) => dispatch(lookupActions.getLookups(customerId, jurisdiction)),
        setSimplePersonDemographics: data => dispatch(interviewActions.setSimplePersonDemographics(data)),
        setPatientHasNoId: () => dispatch(interviewActions.setPatientHasNoId()),
    })
)(AddNewCaseWithUser);
